import immunsupportImg from "@/assets/immunsupport.webp";
import cVitaminImg from "@/assets/c-vitamin.webp";
import solGladjeImg from "@/assets/sol-gladje.webp";
import elektrolyterImg from "@/assets/elektrolyter.webp";
import tropikollagenImg from "@/assets/tropikollagen.webp";

export interface Product {
  id: string;
  name: string;
  shortName: string;
  gradient: string;
  description: string;
  image: string;
}

export interface CartItem {
  product: Product;
  quantity: number;
}

export interface PriceTier {
  tubes: number;
  drinks: number;
  name: string;
  purchasePrice: number;
  subscriptionPrice: number;
  purchasePriceId: string;
  subscriptionPriceId: string;
}

export const products: Product[] = [
  {
    id: "immunsupport",
    name: "Immunsupport",
    shortName: "Immun",
    gradient: "from-[#D8CDE6] to-[#B8A8D4]",
    description: "Stärk ditt immunförsvar",
    image: immunsupportImg,
  },
  {
    id: "c-vitamin",
    name: "C-Vitamin",
    shortName: "C-Vit",
    gradient: "from-[#F5D4B0] to-[#E8B585]",
    description: "Daglig dos av C-vitamin",
    image: cVitaminImg,
  },
  {
    id: "sol-gladje",
    name: "Sol & Glädje",
    shortName: "Sol",
    gradient: "from-[#F5E5B0] to-[#E8D278]",
    description: "D-vitamin för ljusare dagar",
    image: solGladjeImg,
  },
  {
    id: "elektrolyter",
    name: "Elektrolyter",
    shortName: "Elekt",
    gradient: "from-[#DCE8C0] to-[#BCD590]",
    description: "Optimal hydrering",
    image: elektrolyterImg,
  },
  {
    id: "tropikollagen",
    name: "Tropikollagen",
    shortName: "Kollagen",
    gradient: "from-[#F9D878] to-[#F2A65A]",
    description: "Marint kollagen & hyaluronsyra",
    image: tropikollagenImg,
  },
];

export const DRINKS_PER_TUBE = 20;

export const priceTiers: PriceTier[] = [
  { tubes: 8,  drinks: 160,  name: "DROPPEN",  purchasePrice: 800,  subscriptionPrice: 640,  purchasePriceId: "tier_8_purchase",  subscriptionPriceId: "tier_8_subscription" },
  { tubes: 16, drinks: 320,  name: "KÄLLAN",   purchasePrice: 1440, subscriptionPrice: 1120, purchasePriceId: "tier_16_purchase", subscriptionPriceId: "tier_16_subscription" },
  { tubes: 32, drinks: 640,  name: "STRÖMMEN", purchasePrice: 2560, subscriptionPrice: 1920, purchasePriceId: "tier_32_purchase", subscriptionPriceId: "tier_32_subscription" },
  { tubes: 56, drinks: 1120, name: "FLODEN",   purchasePrice: 3920, subscriptionPrice: 2800, purchasePriceId: "tier_56_purchase", subscriptionPriceId: "tier_56_subscription" },
  { tubes: 96, drinks: 1920, name: "OCEANEN",  purchasePrice: 5760, subscriptionPrice: 3840, purchasePriceId: "tier_96_purchase", subscriptionPriceId: "tier_96_subscription" },
];

export type OrderMode = "purchase" | "subscription";

export function getTierForQuantity(totalQty: number): PriceTier | null {
  const sorted = [...priceTiers].sort((a, b) => b.tubes - a.tubes);
  for (const tier of sorted) {
    if (totalQty >= tier.tubes) return tier;
  }
  return null;
}

export function getPrice(totalQty: number, mode: OrderMode): number | null {
  const tier = getTierForQuantity(totalQty);
  if (!tier || totalQty !== tier.tubes) return null;
  return mode === "purchase" ? tier.purchasePrice : tier.subscriptionPrice;
}

export function getNextTier(totalQty: number): PriceTier | null {
  for (const tier of priceTiers) {
    if (tier.tubes > totalQty) return tier;
  }
  return null;
}

export function getExactTier(totalQty: number): PriceTier | null {
  return priceTiers.find((t) => t.tubes === totalQty) || null;
}

export interface Bundle {
  id: string;
  name: string;
  tagline: string;
  tubes: number;
  people: string;
  highlighted?: boolean;
  gradient: string;
}

export const bundles: Bundle[] = [
  {
    id: "droppen",
    name: "DROPPEN",
    tagline: "Testa konceptet med teamet",
    tubes: 8,
    people: "5–15 personer",
    gradient: "from-[#E4EEF2] to-[#CCDFE7]",
  },
  {
    id: "kallan",
    name: "KÄLLAN",
    tagline: "För det lilla kontoret",
    tubes: 16,
    people: "15–30 personer",
    gradient: "from-[#D6E6EC] to-[#B8D2DC]",
  },
  {
    id: "strommen",
    name: "STRÖMMEN",
    tagline: "Perfekt för växande team",
    tubes: 32,
    people: "30–50 personer",
    highlighted: true,
    gradient: "from-[#BCD7E0] to-[#9CC0CD]",
  },
  {
    id: "floden",
    name: "FLODEN",
    tagline: "För hela avdelningen",
    tubes: 56,
    people: "50–80 personer",
    gradient: "from-[#A8C5CE] to-[#85A8B4]",
  },
  {
    id: "oceanen",
    name: "OCEANEN",
    tagline: "Bäst pris per drink",
    tubes: 96,
    people: "80–150 personer",
    gradient: "from-[#92B3BF] to-[#6E95A4]",
  },
];

// Legacy compat
export interface PricingItem {
  id: number;
  name: string;
  price: string;
  period?: string;
  gradient: string;
}
export const packages: PricingItem[] = [];
export const memberships: PricingItem[] = [];
export const dispensers: PricingItem[] = [];
export const refills: PricingItem[] = [];
export const subscriptions: PricingItem[] = [];
export const giftCards: PricingItem[] = [];
